import { useMemo } from "react";
import moment from "moment-timezone";
import styles from "./SplicerAuditReport.module.css";
import useQuestions from "../../hooks/useAuditQuestions";

const useSplicerAuditReport = () => {
  const { splicerToolQuestions } = useQuestions();

  const formatPhoneNumber = (phoneNumber) => {
    // Filter only numbers from the input.
    let cleaned = ("" + phoneNumber).replace(/\D/g, "");

    // Check if the input is of correct length.
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : "";
  };

  const columns = useMemo(
    () => [
      {
        Header: "Splicer Audit Report",
        columns: [
          {
            Header: "Market",
            accessor: "MARKET",
          },
          {
            Header: "LCP/Ring/Areaname",
            accessor: "AREANAME",
          },
          {
            Header: "Contractor",
            accessor: "CONTRACTOR",
          },
          {
            Header: "Foreman",
            accessor: "FOREMAN",
          },
          {
            Header: "In-House Splicer",
            accessor: "IN_HOUSE_SPLICER",
          },
          {
            Header: "Auditor",
            accessor: "AUDITOR",
          },
          {
            Header: "Audit Date",
            accessor: (d) => {
              if (d.AUDIT_DATE) {
                return moment(d.AUDIT_DATE)
                  .local()
                  .format("MM-DD-YYYY hh:mm A");
              } else {
                return "";
              }
            },
          },
          {
            Header: "Audit ID",
            accessor: "AUDIT_GUID",
          },
          {
            Header: "Auditor Email",
            accessor: "AUDITOR_EMAIL",
          },
          {
            Header: "Crew Enabled",
            accessor: "ENABLED",
          },
          {
            Header: "Crew Type",
            accessor: "TYPE",
          },
          {
            Header: "Drill Operator",
            accessor: "DRILL_OPERATOR",
          },
          {
            Header: "Foreman Email",
            accessor: "FOREMAN_EMAIL",
          },
          {
            Header: "In-House Splicer Email",
            accessor: "IN_HOUSE_SPLICER_EMAIL",
          },
          {
            Header: "Foreman Phone",
            //accessor: "FOREMAN_PHONE",
            accessor: (d) => {
              if (d.FOREMAN_PHONE) {
                return formatPhoneNumber(d.FOREMAN_PHONE);
              } else {
                return "";
              }
            },
          },
          {
            Header: "Locator",
            accessor: "LOCATOR",
          },
          {
            Header: "Latitude",
            accessor: "LATITUDE",
          },
          {
            Header: "Longitude",
            accessor: "LONGITUDE",
          },
          {
            Header: "Location",
            accessor: (d) => {
              if (d.LATITUDE && d.LONGITUDE) {
                let coordinatesLink = `https://www.google.com/maps/search/?api=1&query=${d.LATITUDE},${d.LONGITUDE}`;
                return (
                  <button
                    className={styles.cellLinkButton}
                    onClick={() => window.open(coordinatesLink)}
                  >
                    Google Map Location
                  </button>
                );
              } else return "N/A";
            },
          },
          {
            Header: "Supervisor",
            accessor: "SUPERVISOR",
          },
          {
            Header: "Reviewed By",
            accessor: "REVIEWED_BY",
          },
          {
            Header: "Reviewed Date",
            accessor: (d) => {
              if (d.REVIEWED_DATE) {
                return moment(d.REVIEWED_DATE)
                  .local()
                  .format("MM-DD-YYYY hh:mm A");
              } else {
                return "";
              }
            },
          },
          {
            Header: "Reviewed Status",
            accessor: "REVIEWED_STATUS",
          },
          {
            Header: "Reviewer Email",
            accessor: "REVIEWER_EMAIL",
          },
          {
            Header: "Reviewed Notes",
            accessor: "REVIEWED_NOTES",
          },
          {
            Header: "HOUSE",
            accessor: "HOUSE",
          },
          {
            Header: "FSPL",
            accessor: "FSPL",
          },
          {
            Header: "WAP",
            accessor: "WAP",
          },
          {
            Header: "PPE",
            accessor: "PPE",
          },
          // Inventory Tool List
          {
            Header: splicerToolQuestions.bareFiberAdapterMakeModel,
            accessor: (d) => {
              return d?.BARE_FIBER_ADAPTER_MAKE_MODEL
                ? d.BARE_FIBER_ADAPTER_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.bufferTubeMakeModel,
            accessor: (d) => {
              return d?.BUFFER_TUBE_MAKE_MODEL ? d.BUFFER_TUBE_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.conesMakeModel,
            accessor: (d) => {
              return d?.CONES_MAKE_MODEL ? d.CONES_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.coreTubeEntryKitMakeModel,
            accessor: (d) => {
              return d?.CORE_TUBE_ENTRY_KIT_MAKE_MODEL
                ? d.CORE_TUBE_ENTRY_KIT_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.drill,
            accessor: (d) => {
              return d?.DRILL_MAKE_MODEL ? d.DRILL_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.exfoLaunchBagMakeModel,
            accessor: (d) => {
              return d?.EXFO_LAUNCH_BAG_MAKE_MODEL
                ? d.EXFO_LAUNCH_BAG_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.fiberMakeModel,
            accessor: (d) => {
              return d?.FIBER_MAKE_MODEL ? d.FIBER_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.fiberSerial,
            accessor: (d) => {
              return d?.FIBER_SERIAL ? d.FIBER_SERIAL : "";
            },
          },
          {
            Header: splicerToolQuestions.foDropSlitterMakeModel,
            accessor: (d) => {
              return d?.FIBER_OPTIC_DROP_SPLITTER_MAKE_MODEL
                ? d.FIBER_OPTIC_DROP_SPLITTER_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.fishTapeMakeModel,
            accessor: (d) => {
              return d?.FISH_TAPE_MAKE_MODEL ? d.FISH_TAPE_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.fsMachineRibbonMakeModel,
            accessor: (d) => {
              return d?.FUSION_SPLICE_MACHINE_RIBBON_MAKE_MODEL
                ? d.FUSION_SPLICE_MACHINE_RIBBON_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.fsMachineRibbonSerial,
            accessor: (d) => {
              return d?.FUSION_SPLICE_MACHINE_RIBBON_SERIAL
                ? d.FUSION_SPLICE_MACHINE_RIBBON_SERIAL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.fsMachineSingleMakeModel,
            accessor: (d) => {
              return d?.FUSION_SPLICE_MACHINE_SINGLE_MAKE_MODEL
                ? d.FUSION_SPLICE_MACHINE_SINGLE_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.fsMachineSingleSerial,
            accessor: (d) => {
              return d?.FUSION_SPLICE_MACHINE_SINGLE_SERIAL
                ? d.FUSION_SPLICE_MACHINE_SINGLE_SERIAL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.handToolsMakeModel,
            accessor: (d) => {
              return d?.HAND_TOOLS_MAKE_MODEL ? d.HAND_TOOLS_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.harnessMakeModel,
            accessor: (d) => {
              return d?.HARNESS_MAKE_MODEL ? d.HARNESS_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.ladder28ftMakeModel,
            accessor: (d) => {
              return d?.LADDER_LONG_MAKE_MODEL ? d.LADDER_LONG_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.ladder6ftMakeModel,
            accessor: (d) => {
              return d?.LADDER_SHORT_MAKE_MODEL
                ? d.LADDER_SHORT_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.midspanSplitterMakeModel,
            accessor: (d) => {
              return d?.MIDSPAN_SPLITTER_MAKE_MODEL
                ? d.MIDSPAN_SPLITTER_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.opticalLossMakeModel1,
            accessor: (d) => {
              return d?.OPTICAL_LOSS_MAKE_MODEL_1
                ? d.OPTICAL_LOSS_MAKE_MODEL_1
                : "";
            },
          },
          {
            Header: splicerToolQuestions.opticalLossSerial1,
            accessor: (d) => {
              return d?.OPTICAL_LOSS_SERIAL_1 ? d.OPTICAL_LOSS_SERIAL_1 : "";
            },
          },
          {
            Header: splicerToolQuestions.opticalLossMakeModel2,
            accessor: (d) => {
              return d?.OPTICAL_LOSS_MAKE_MODEL_2
                ? d.OPTICAL_LOSS_MAKE_MODEL_2
                : "";
            },
          },
          {
            Header: splicerToolQuestions.opticalLossSerial2,
            accessor: (d) => {
              return d?.OPTICAL_LOSS_SERIAL_2 ? d.OPTICAL_LOSS_SERIAL_2 : "";
            },
          },
          {
            Header: splicerToolQuestions.otdrMakeModel,
            accessor: (d) => {
              return d?.OTDR_MAKE_MODEL ? d.OTDR_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.otdrSerial,
            accessor: (d) => {
              return d?.OTDR_SERIAL ? d.OTDR_SERIAL : "";
            },
          },
          {
            Header: splicerToolQuestions.safetyVestAndHardHatMakeModel,
            accessor: (d) => {
              return d?.SAFETY_VEST_HARD_HAT_MAKE_MODEL
                ? d.SAFETY_VEST_HARD_HAT_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.signAndHolderMakeModel,
            accessor: (d) => {
              return d?.SIGN_AND_HOLDER_MAKE_MODEL
                ? d.SIGN_AND_HOLDER_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.splicerTentMakeModel,
            accessor: (d) => {
              return d?.SPLICER_TENT_MAKE_MODEL
                ? d.SPLICER_TENT_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.splicingTableMakeModel,
            accessor: (d) => {
              return d?.SPLICING_TABLE_MAKE_MODEL
                ? d.SPLICING_TABLE_MAKE_MODEL
                : "";
            },
          },
          {
            Header: splicerToolQuestions.toolsetMakeModel,
            accessor: (d) => {
              return d?.TOOLSET_MAKE_MODEL ? d.TOOLSET_MAKE_MODEL : "";
            },
          },
          {
            Header: splicerToolQuestions.trailerTypeAndNum,
            accessor: (d) => {
              return d?.TRAILER_TYPE_AND_NUM ? d.TRAILER_TYPE_AND_NUM : "";
            },
          },
          {
            Header: splicerToolQuestions.vehicleNumber,
            accessor: (d) => {
              return d?.VECHICLE_NUMBER ? d.VECHICLE_NUMBER : "";
            },
          },
          {
            Header: splicerToolQuestions.vehicleType,
            accessor: (d) => {
              return d?.VEHICLE_TYPE ? d.VEHICLE_TYPE : "";
            },
          },
          {
            Header: splicerToolQuestions.toolNotes,
            accessor: (d) => {
              return d?.NOTES ? d.NOTES : "";
            },
          },
        ],
      },
    ],
    //eslint-disable-next-line
    []
  );
  return {
    columns,
  };
};

export default useSplicerAuditReport;
